import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
	fetchAggregateMetrics,
	fetchPageMetrics,
	fetchSyncStatus,
	type Period,
	type SyncStatus,
} from "@/services/searchPerformanceService";
import { useTenantStore } from "@/stores/useTenantStore";
import { Button } from "@/components/ui/button";
import { SectionPanel } from "@/components/dashboard/SectionPanel";
import { PeriodSelector } from "@/components/dashboard/PeriodSelector";
import { IntegrationStatePanel } from "@/components/dashboard/IntegrationStatePanel";
import { SearchPagesTable } from "@/components/dashboard/tables/SearchPagesTable";
import { BrandedSplitStrip } from "@/components/dashboard/tables/BrandedSplitStrip";

const PERIOD_DAYS: Record<Period, number> = { "7d": 7, "28d": 28, "90d": 90 };

/** Sync statuses meaning a sync is currently in flight. */
const SYNC_IN_PROGRESS_STATUSES = new Set(["running", "syncing", "in_progress"]);
/** Failed-sync messages that mean the agency account lacks property access. */
const GSC_AUTH_ERROR_PATTERN = /permission|forbidden|403|access[ -]?denied/i;
/** Terminal sync statuses — nothing is in flight, so a stale range is NOT "syncing". */
const SYNC_TERMINAL_STATUSES = new Set(["completed", "failed", "never_synced"]);

function parsePeriod(raw: string | null): Period {
	return raw === "7d" || raw === "90d" ? raw : "28d";
}

type TopPagesBodyState =
	| "loading"
	| "never_started"
	| "in_progress"
	| "permission_denied"
	| "errored"
	| "partial"
	| "ready"
	| "empty";

/**
 * Rows exist but a sync is actually in flight, still catching coverage up.
 * A completed-but-stale range is NOT short coverage — no sync is running, and
 * the panel caption already states the through-date honestly.
 */
function isCoverageShort(syncStatus: SyncStatus): boolean {
	if (SYNC_IN_PROGRESS_STATUSES.has(syncStatus.status)) return true;
	if (SYNC_TERMINAL_STATUSES.has(syncStatus.status)) return false;
	// Unknown (activity) status: only caveat when the coverage end is missing/unparseable.
	if (!syncStatus.date_range_end) return true;
	return Number.isNaN(Date.parse(syncStatus.date_range_end));
}

/** Activation state map (doc 15 §13): keyed off sync status + row presence. */
function selectBodyState(
	syncStatus: SyncStatus | undefined,
	pageCount: number,
	isLoading: boolean,
): TopPagesBodyState {
	if (isLoading) return "loading";
	if (pageCount > 0) {
		return syncStatus && isCoverageShort(syncStatus) ? "partial" : "ready";
	}
	if (syncStatus?.status === "failed") {
		return GSC_AUTH_ERROR_PATTERN.test(syncStatus.error_message ?? "")
			? "permission_denied"
			: "errored";
	}
	if (SYNC_IN_PROGRESS_STATUSES.has(syncStatus?.status ?? "")) return "in_progress";
	if (syncStatus?.status === "never_synced") return "never_started";
	return "empty";
}

export default function TopPagesPage() {
	const { currentTenant } = useTenantStore();
	const tenantScope = currentTenant ?? "no-tenant";
	const [searchParams, setSearchParams] = useSearchParams();
	const period = parsePeriod(searchParams.get("period"));
	const [sortBy, setSortBy] = useState("impressions");
	const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

	const setPeriod = (p: Period) => {
		const next = new URLSearchParams(searchParams);
		next.set("period", p);
		setSearchParams(next, { replace: true });
	};

	const { data: pageData, isLoading: pagesLoading, refetch: pagesRefetch } = useQuery({
		queryKey: ["search-performance", tenantScope, "metrics", period, sortBy, sortDir],
		queryFn: () => fetchPageMetrics(currentTenant, period, sortBy, sortDir),
		staleTime: 5 * 60 * 1000,
		enabled: !!currentTenant,
	});
	const { data: syncStatus, isLoading: syncStatusLoading, refetch: syncStatusRefetch } = useQuery({
		queryKey: ["search-performance", tenantScope, "sync-status"],
		queryFn: () => fetchSyncStatus(currentTenant),
		staleTime: 60 * 1000,
		enabled: !!currentTenant,
	});
	// B2/G-O1 branded split — same aggregate route Home consumes. Fields are
	// nullable (null = not classifiable, NEVER 0); the strip renders them as
	// passed-through, so `?? null`, never `?? 0`.
	const { data: aggregate } = useQuery({
		queryKey: ["search-performance", tenantScope, "aggregate", period],
		queryFn: () => fetchAggregateMetrics(currentTenant, period),
		staleTime: 5 * 60 * 1000,
		enabled: !!currentTenant,
	});

	const handleSort = (col: string) => {
		if (sortBy === col) {
			setSortDir((d) => (d === "desc" ? "asc" : "desc"));
		} else {
			setSortBy(col);
			setSortDir("desc");
		}
	};

	const handleRecheck = () => {
		syncStatusRefetch();
		pagesRefetch();
	};

	const pages = pageData?.pages ?? [];
	const bodyState = selectBodyState(syncStatus, pages.length, pagesLoading || syncStatusLoading);
	const gscFailureMessage = syncStatus?.error_message || "Google Search Console sync failed.";

	return (
		<div className="w-full space-y-6">
			<div className="flex flex-wrap items-center justify-between gap-4">
				<h1 className="text-dash-page font-semibold text-foreground">Top pages</h1>
				<PeriodSelector value={period} onChange={setPeriod} />
			</div>
			<SectionPanel
				title={`Search Performance by Page (${pageData?.total ?? 0})`}
				caption={pages.length > 0 ? `Last ${PERIOD_DAYS[period]} days · through ${syncStatus?.date_range_end ?? "—"} · Search Console lags ~3 days` : undefined}
				surfaceEmphasis="primary"
			>
				{bodyState === "loading" ? (
					<IntegrationStatePanel state="loading" skeleton="rows" />
				) : bodyState === "never_started" ? (
					<div className="space-y-3 p-6">
						<p className="text-sm text-foreground">
							Top pages come from Search Console — connect to see which pages earn your clicks.
						</p>
						<div>
							<Button asChild className="bg-coral text-primary-foreground hover:bg-coral-hover">
								<Link to="/connect/search-console">Connect Search Console</Link>
							</Button>
						</div>
					</div>
				) : bodyState === "in_progress" ? (
					<div className="space-y-3">
						<p className="px-5 pt-4 text-sm text-muted-foreground">
							Syncing… · Search Console lags ~3 days
						</p>
						<IntegrationStatePanel state="loading" skeleton="rows" />
					</div>
				) : bodyState === "permission_denied" ? (
					<div className="space-y-3 p-6">
						<p className="text-sm text-foreground">
							We can't see your Search Console property yet — grant access in Google's Search Console, then re-check.
						</p>
						<div>
							<Button variant="outline" size="sm" onClick={handleRecheck}>
								Re-check
							</Button>
						</div>
					</div>
				) : bodyState === "errored" ? (
					<IntegrationStatePanel state="error" errorReason={gscFailureMessage} onRetry={handleRecheck} />
				) : bodyState === "empty" ? (
					<IntegrationStatePanel state="empty" message="No page metrics found for this period" />
				) : (
					<div>
						{bodyState === "partial" && (
							<p className="border-b px-5 py-2 text-dash-context text-muted-foreground">
								Data through {syncStatus?.date_range_end ?? "—"} — syncing toward 90 days
							</p>
						)}
						{/* The split strip lives inside the table panel, above the table, and
							    only in this branch — ready/partial by construction. Dark states
								    (never_started/in_progress/permission_denied/errored/empty) never
								    reach here, so they show nothing new (D1 bodies unchanged). */}
						<BrandedSplitStrip
							brandedClicks={aggregate?.branded_clicks ?? null}
							nonbrandedClicks={aggregate?.nonbranded_clicks ?? null}
						/>
						<SearchPagesTable
							pages={pages}
							sortBy={sortBy}
							sortDir={sortDir}
							onSort={handleSort}
							period={period}
							tenantId={currentTenant}
							positionHeader="Avg position (unweighted)"
						/>
					</div>
				)}
			</SectionPanel>
		</div>
	);
}
